/**
 * `<RouteOpResultToast />` — the *only* place dialogs render `OperationResult`
 * outcomes. Centralising this guarantees every dialog speaks the same UX
 * language: one place to fix the wording, one place to enforce that every
 * `RouteOpError.kind` has a UI (the `assertNever` default in the switch makes
 * that a compile error).
 *
 * The hook (`useRouteOperation`) produces `OperationResult`; this component
 * fires the toast as a side-effect of the result changing. We don't render
 * any DOM — toasts are global.
 */
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'

import { assertNever } from 'types/entities/shared/result'

import type { OperationResult, RouteOpError } from 'types/modules/routing'

interface Props {
  readonly result: OperationResult | null
}

const formatSuccess = (result: OperationResult): string => {
  const phaseCount = result.executedPhases.length
  const checkedCount = result.verifyReport?.checked.length ?? 0
  const stopCount = result.verifyReport?.checked.reduce((sum, c) => sum + c.committedCount, 0) ?? 0
  if (checkedCount > 0) return `Saved (${stopCount} stops, ${phaseCount} phases)`
  return `Saved (${phaseCount} phases)`
}

const formatPartial = (result: OperationResult): string => {
  const cascade = result.cascadeReport
  if (cascade) return `Saved with warnings (${cascade.succeeded} of ${cascade.attempted} cascaded dailies)`
  return 'Saved with warnings'
}

/**
 * Translate every `RouteOpError.kind` to plain English. Adding a new kind to
 * `RouteOpError` without a branch here fails compilation thanks to
 * `assertNever`.
 */
export const formatError = (error: RouteOpError): string => {
  switch (error.kind) {
    case 'PAYLOAD_COUNT_MISMATCH':
      return `Save blocked: payload had ${error.actual} stops, expected ${error.expected} (missing ${error.missing.length}, extra ${error.extra.length}).`
    case 'DUPLICATE_STOP_IN_PAYLOAD':
      return `Save blocked: ${error.duplicates.length} duplicate stop ID${error.duplicates.length === 1 ? '' : 's'} in payload.`
    case 'DUPLICATE_SEQUENCE_VALUE':
      return `Save blocked: ${error.pairs.length} duplicate sequence value${error.pairs.length === 1 ? '' : 's'}.`
    case 'MASTER_COUNT_DRIFT_PREEMPTIVE':
      return `Save blocked: master count drifted (${error.before} → ${error.after}). Refresh and retry.`
    case 'BE_REJECTED':
      // A 403 body already names the missing permission and who can grant it; a prefix only buries it.
      if (error.status === 403) return error.message
      return `Server rejected the ${error.phase} phase: ${error.message}`
    case 'CONCURRENT_COMMIT_DETECTED':
      return 'Another save is still in progress on this route. Please wait and try again.'
    case 'STRATEGY_FALLBACK':
      return `Strategy fallback ${error.from} → ${error.to}: ${error.reason}`
    case 'PHANTOM_STOP_DETECTED':
      return `Verify failed: ${error.extra.length} phantom stop${error.extra.length === 1 ? '' : 's'} detected after commit.`
    case 'ORPHANED_ON_REQUEST':
      return `Save blocked: ${error.stopIds.length} on-request stop${error.stopIds.length === 1 ? '' : 's'} would be orphaned.`
    case 'PRECONDITION_FAILED':
      return `Save blocked: ${error.reason}`
    default:
      return assertNever(error)
  }
}

// For dialogs that fire their own toast rather than mounting <RouteOpResultToast />.
export const routeOpFailureMessage = (result: OperationResult, fallback: string): string =>
  result.error ? formatError(result.error.cause) : fallback

export function RouteOpResultToast({ result }: Props): null {
  const lastShownRef = useRef<OperationResult | null>(null)

  useEffect(() => {
    if (!result || result === lastShownRef.current) return
    lastShownRef.current = result

    if (result.success && !result.partial) {
      toast.success(formatSuccess(result))
      return
    }

    if (result.success && result.partial) {
      toast.warning(formatPartial(result))
      return
    }

    if (result.error) {
      toast.error(formatError(result.error.cause))
      return
    }

    // Failure with no `error` shouldn't happen — the executor always sets one
    // when `success` is false. Keep a generic fallback so the user isn't left
    // wondering whether the save took effect.
    toast.error('Save failed (no further detail available).')
  }, [result])

  return null
}
